import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Users, TrendingUp, Loader2, ChevronLeft, Download, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BarRow = ({ label, value, max, color = 'bg-orange-500' }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5 group">
      <span className="text-sm text-gray-700 w-40 truncate font-medium" title={label}>{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
        <div className={`${color} h-full rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 1.5)}%` }} />
      </div>
      <span className="text-sm font-bold text-gray-800 w-10 text-right">{value}</span>
    </div>
  );
};

const ReportSection = ({ title, data, color, icon, maxItems = 15 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data || Object.keys(data).length === 0) return null;
  const entries = Object.entries(data);
  const maxVal = Math.max(...entries.map(([, v]) => v));
  const visible = expanded ? entries : entries.slice(0, maxItems);
  const total = entries.reduce((s, [, v]) => s + v, 0);

  return (
    <Card className="p-5" data-testid={`report-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-bold text-sm text-gray-900">{title}</h3>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{entries.length} groups, {total} total</span>
        </div>
      </div>
      <div className="space-y-0.5">
        {visible.map(([label, value]) => (
          <BarRow key={label} label={label} value={value} max={maxVal} color={color} />
        ))}
      </div>
      {entries.length > maxItems && (
        <button onClick={() => setExpanded(!expanded)} className="mt-3 text-xs text-orange-600 hover:text-orange-800 font-medium">
          {expanded ? 'Show less' : `Show all ${entries.length} entries`}
        </button>
      )}
    </Card>
  );
};

const SummaryCard = ({ label, value, sub, color }) => (
  <Card className={`p-4 ${color}`}>
    <p className="text-xs font-medium opacity-70">{label}</p>
    <p className="text-3xl font-bold">{value ?? 0}</p>
    {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
  </Card>
);

export default function PSReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/ps/admin/reports`, { headers })
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  }, []);

  const exportReportCSV = () => {
    if (!data) return;
    const rows = [['Section', 'Category', 'Value']];
    const addSection = (section, obj) => {
      Object.entries(obj).forEach(([k, v]) => {
        if (typeof v === 'object') {
          Object.entries(v).forEach(([label, count]) => rows.push([section, `${k}: ${label}`, count]));
        }
      });
    };
    addSection('Families', data.families);
    addSection('All Candidates', data.candidates);
    addSection('Male Candidates', data.male);
    addSection('Female Candidates', data.female);
    if (data.engagement) {
      rows.push(['Engagement', 'Total Engaged', data.engagement.engagedTotal]);
      rows.push(['Engagement', 'Total Available', data.engagement.availableTotal]);
      rows.push(['Engagement', 'Engaged Male', data.engagement.engagedMale]);
      rows.push(['Engagement', 'Engaged Female', data.engagement.engagedFemale]);
      rows.push(['Engagement', 'Available Male', data.engagement.availableMale]);
      rows.push(['Engagement', 'Available Female', data.engagement.availableFemale]);
      Object.entries(data.engagement.availableByNukh || {}).forEach(([k, v]) => rows.push(['Engagement', `Available by Nukh: ${k}`, v]));
      Object.entries(data.engagement.availableByPlace || {}).forEach(([k, v]) => rows.push(['Engagement', `Available by Place: ${k}`, v]));
      Object.entries(data.engagement.engagedByNukh || {}).forEach(([k, v]) => rows.push(['Engagement', `Engaged by Nukh: ${k}`, v]));
      Object.entries(data.engagement.engagedByPlace || {}).forEach(([k, v]) => rows.push(['Engagement', `Engaged by Place: ${k}`, v]));
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `ps2026_reports_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!data) return <div className="p-6 text-center text-gray-500">No report data available.</div>;

  const { families, candidates, male, female, attendance, engagement } = data;

  return (
    <div className="p-6" data-testid="ps-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => window.history.back()}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-orange-600" /> PS 2026 Reports
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={exportReportCSV} data-testid="ps-reports-export">
          <Download className="w-4 h-4 mr-1" /> Export Report CSV
        </Button>
      </div>

      {/* Attendance Counts */}
      {attendance && (
        <>
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
            <Users className="w-5 h-5 text-green-600" /> Count of Persons Attending the Event
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="attendance-counts">
            <SummaryCard label="Total Attending" value={attendance.totalAttendees} color="bg-green-50 text-green-700" />
            <SummaryCard label="Male Candidates" value={attendance.maleCandidates} color="bg-sky-50 text-sky-700" />
            <SummaryCard label="Female Candidates" value={attendance.femaleCandidates} color="bg-pink-50 text-pink-700" />
            <SummaryCard label="Male Attendees" value={attendance.maleOtherAttendees} color="bg-blue-50 text-blue-700" />
            <SummaryCard label="Female Attendees" value={attendance.femaleOtherAttendees} color="bg-rose-50 text-rose-700" />
            {attendance.unknownGenderAttendees > 0 && (
              <SummaryCard label="Other Attendees" value={attendance.unknownGenderAttendees} color="bg-gray-50 text-gray-700" />
            )}
          </div>
        </>
      )}

      {/* Engagement Status */}
      {engagement && (
        <>
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
            <Heart className="w-5 h-5 text-rose-500" /> Engagement & Availability
            <span className="text-xs font-normal text-gray-500">
              (Candidates marked &quot;Engaged&quot; by admin are excluded from public directory contact reveal)
            </span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="engagement-counts">
            <SummaryCard label="Total Engaged 💍" value={engagement.engagedTotal} sub="All genders" color="bg-rose-50 text-rose-700" />
            <SummaryCard label="Total Available" value={engagement.availableTotal} sub="All genders" color="bg-emerald-50 text-emerald-700" />
            <SummaryCard label="Engaged Male" value={engagement.engagedMale} color="bg-sky-50 text-sky-700" />
            <SummaryCard label="Engaged Female" value={engagement.engagedFemale} color="bg-pink-50 text-pink-700" />
            <SummaryCard label="Available Male" value={engagement.availableMale} color="bg-blue-50 text-blue-700" />
            <SummaryCard label="Available Female" value={engagement.availableFemale} color="bg-fuchsia-50 text-fuchsia-700" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <ReportSection title="Available by Nukh" data={engagement.availableByNukh} color="bg-emerald-500" icon={<div className="w-3 h-3 rounded-full bg-emerald-500" />} />
            <ReportSection title="Available by Place" data={engagement.availableByPlace} color="bg-teal-500" icon={<div className="w-3 h-3 rounded-full bg-teal-500" />} />
            <ReportSection title="Engaged by Nukh" data={engagement.engagedByNukh} color="bg-rose-500" icon={<div className="w-3 h-3 rounded-full bg-rose-500" />} />
            <ReportSection title="Engaged by Place" data={engagement.engagedByPlace} color="bg-pink-500" icon={<div className="w-3 h-3 rounded-full bg-pink-500" />} />
          </div>
        </>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <SummaryCard label="Total Families" value={families.total} color="bg-blue-50 text-blue-700" />
        <SummaryCard label="Total Candidates" value={candidates.total} color="bg-indigo-50 text-indigo-700" />
        <SummaryCard label="Male Candidates" value={male.total} color="bg-sky-50 text-sky-700" />
        <SummaryCard label="Female Candidates" value={female.total} color="bg-pink-50 text-pink-700" />
        <SummaryCard label="Male : Female Ratio" value={male.total && female.total ? `${(male.total / female.total).toFixed(1)} : 1` : 'N/A'} color="bg-purple-50 text-purple-700" />
      </div>

      {/* Family Reports */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <Users className="w-5 h-5 text-blue-600" /> Family Reports
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <ReportSection title="Families by Status" data={families.byStatus} color="bg-blue-500" icon={<div className="w-3 h-3 rounded-full bg-blue-500" />} />
        <ReportSection title="Families by Place" data={families.byPlace} color="bg-teal-500" icon={<div className="w-3 h-3 rounded-full bg-teal-500" />} />
      </div>

      {/* Candidate Reports */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <TrendingUp className="w-5 h-5 text-indigo-600" /> All Candidates Reports
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <ReportSection title="Candidates by Status" data={candidates.byStatus} color="bg-indigo-500" icon={<div className="w-3 h-3 rounded-full bg-indigo-500" />} />
        <ReportSection title="Candidates by Gender" data={candidates.byGender} color="bg-violet-500" icon={<div className="w-3 h-3 rounded-full bg-violet-500" />} />
        <ReportSection title="Candidates by Place" data={candidates.byPlace} color="bg-cyan-500" icon={<div className="w-3 h-3 rounded-full bg-cyan-500" />} />
      </div>

      {/* Female Candidates Reports */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <span className="w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center text-white text-xs font-bold">F</span> Female Candidates Breakdown ({female.total})
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <ReportSection title="Females by Nukh" data={female.byNukh} color="bg-pink-500" icon={<div className="w-3 h-3 rounded-full bg-pink-500" />} />
        <ReportSection title="Females by Place" data={female.byPlace} color="bg-rose-500" icon={<div className="w-3 h-3 rounded-full bg-rose-500" />} />
        <ReportSection title="Females by Education" data={female.byEducation} color="bg-fuchsia-500" icon={<div className="w-3 h-3 rounded-full bg-fuchsia-500" />} />
        <ReportSection title="Females by Marital Status" data={female.byMaritalStatus} color="bg-pink-400" icon={<div className="w-3 h-3 rounded-full bg-pink-400" />} />
        <ReportSection title="Females by Age Group" data={female.byAgeGroup} color="bg-rose-400" icon={<div className="w-3 h-3 rounded-full bg-rose-400" />} />
      </div>

      {/* Male Candidates Reports */}
      <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2 border-b pb-2">
        <span className="w-5 h-5 rounded-full bg-sky-500 flex items-center justify-center text-white text-xs font-bold">M</span> Male Candidates Breakdown ({male.total})
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <ReportSection title="Males by Nukh" data={male.byNukh} color="bg-sky-500" icon={<div className="w-3 h-3 rounded-full bg-sky-500" />} />
        <ReportSection title="Males by Place" data={male.byPlace} color="bg-blue-500" icon={<div className="w-3 h-3 rounded-full bg-blue-500" />} />
        <ReportSection title="Males by Education" data={male.byEducation} color="bg-indigo-400" icon={<div className="w-3 h-3 rounded-full bg-indigo-400" />} />
        <ReportSection title="Males by Marital Status" data={male.byMaritalStatus} color="bg-sky-400" icon={<div className="w-3 h-3 rounded-full bg-sky-400" />} />
        <ReportSection title="Males by Age Group" data={male.byAgeGroup} color="bg-blue-400" icon={<div className="w-3 h-3 rounded-full bg-blue-400" />} />
      </div>
    </div>
  );
}
